import React from 'react';
import {
  Flex,
  useColorMode,
  useIsMobileView,
  Text,
  color,
  typography,
} from 'deepstash-ui';
import { StoryApiResponse } from 'api/api.types';
import ReactHtmlParser from 'react-html-parser';
import { findIndexes } from 'utils/StringUtils';
import StoryIdeaCard from './StoryIdeaCard';
import StoryPageTOC from './StoryPageTOC';
import LandingProSection from 'src/page-components/landing/components-v2/LandingProSection';
import NewCollectionCarousel from 'src/components/collection/NewCollectionCarousel';
import StoryPageRelatedStories from './StoryPageRelatedStories';
import { makeTransformHtmlParserStoryContent } from 'utils/html-utils';
import { useRouter } from 'next/router';

interface StoryPageContentProps {
  story: StoryApiResponse;
}

const StoryPageContent: React.FC<StoryPageContentProps> = ({ story }) => {
  const isMobileView = useIsMobileView();
  const { colorMode } = useColorMode();
  const router = useRouter();

  // Saving the indexes of sections in order to modify the order of rendering on the page
  const htmlAux = React.useMemo(() => {
    const indexesOfStartH2 = findIndexes({
      originalString: story.content,
      searchString: '<h2 ',
    });
    const indexesOfEndH2 = findIndexes({
      originalString: story.content,
      searchString: '</h2>',
    });

    const indexesOfStartP = findIndexes({
      originalString: story.content,
      searchString: '<p>',
    });
    const indexesOfEndP = findIndexes({
      originalString: story.content,
      searchString: '</p>',
    });

    const h2Html: { start: number; end: number }[] = [];
    indexesOfStartH2.forEach((value, index) => {
      h2Html.push({ start: value, end: indexesOfEndH2[index] });
    });

    const pHtml: { start: number; end: number }[] = [];
    indexesOfStartP.forEach((value, index) => {
      pHtml.push({ start: value, end: indexesOfEndP[index] });
    });

    return { h2Html, pHtml };
  }, [story.content]);

  const firstParagraphParsed = React.useMemo(() => {
    return ReactHtmlParser(story.content.slice(0, htmlAux.h2Html[0].start), {
      transform: makeTransformHtmlParserStoryContent({
        colorMode: colorMode,
        isMobileView: isMobileView,
        router,
      }),
    });
  }, [story.content, htmlAux, colorMode, isMobileView]);

  const firstSectionParsed = React.useMemo(() => {
    return ReactHtmlParser(
      story.content.slice(htmlAux.h2Html[0].start, htmlAux.h2Html[1].start),
      {
        transform: makeTransformHtmlParserStoryContent({
          colorMode: colorMode,
          isMobileView: isMobileView,
          router,
        }),
      },
    );
  }, [story.content, htmlAux, colorMode, isMobileView]);

  const restOfContentParsed = React.useMemo(() => {
    return ReactHtmlParser(story.content.slice(htmlAux.h2Html[1].start), {
      transform: makeTransformHtmlParserStoryContent({
        colorMode: colorMode,
        isMobileView: isMobileView,
        router,
      }),
    });
  }, [colorMode, isMobileView]);

  return (
    <Flex
      flexDirection={isMobileView ? 'column' : 'row'}
      width={'100%'}
      gridColumnGap={32}
    >
      <Flex
        flex={1}
        flexDirection="column"
        {...(isMobileView ? { px: 16 } : {})}
      >
        <StoryPageTOC story={story} />
      </Flex>
      <Flex flex={4} flexDirection="column">
        <Flex width="100%" {...(isMobileView ? { px: 16 } : {})}>
          <Text
            {...typography.primaryTextFonts.medium.regular}
            color={color[colorMode].text}
          >
            {firstParagraphParsed}
          </Text>
        </Flex>
        {story.collections !== null &&
        story.collections !== undefined &&
        story.collections.length > 0 ? (
          <Flex width="100%" mt={32} flexDirection="column">
            <Text
              {...typography.primaryHeadingFonts.medium.bold}
              lineHeight="auto"
              color={color[colorMode].text}
              {...(isMobileView ? { mx: 16 } : {})}
            >
              {'Become unstoppable with these Deepstash Collections'}
            </Text>
            <NewCollectionCarousel
              collections={story.collections.slice(0, 4)}
              mt={16}
              justifyContent="flex-start"
            />
          </Flex>
        ) : null}
        <Flex mt={32} {...(isMobileView ? { px: 16 } : {})}>
          <Text
            {...typography.primaryTextFonts.medium.regular}
            color={color[colorMode].text}
          >
            {firstSectionParsed}
          </Text>
        </Flex>
        {isMobileView ? (
          <Flex flexDirection="column" width={'100%'} px={16}>
            <StoryIdeaCard story={story} />
            <Flex width={'100%'}>
              <Text color={color[colorMode].text}>{restOfContentParsed}</Text>
            </Flex>
          </Flex>
        ) : (
          <div style={{ width: '100%' }}>
            <Flex
              style={{
                width: 400,
                float: 'right',
                marginTop: '3em',
              }}
            >
              <StoryIdeaCard story={story} />
            </Flex>
            <Text
              {...typography.primaryTextFonts.medium.regular}
              color={color[colorMode].text}
            >
              {restOfContentParsed}
            </Text>
          </div>
        )}
        <LandingProSection
          showProComparison={false}
          trialInfoStyleProps={{ px: { sm: 16, md: 32 } }}
          mt={32}
        />
        {story.similar_stories !== undefined &&
        story.similar_stories !== null &&
        story.similar_stories.length > 0 ? (
          <StoryPageRelatedStories
            mt={88}
            stories={story.similar_stories.slice(0, 3)}
          />
        ) : null}
      </Flex>
    </Flex>
  );
};

export default StoryPageContent;
